// src/components/Widgets/StreakWidget.jsx
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import WidgetHeader from "../Common/WidgetHeader";
import { useStreaks } from "../../contexts/StreaksContext"; // Vault-backed context
import { navigateTo, ROUTES } from "../../lib/nav";
import { useTheme } from "../../core/theme/ThemeProvider";

function sameLocalDay(a, b = new Date()) {
  if (!a) return false;
  const A = new Date(a);
  return (
    A.getFullYear() === b.getFullYear() &&
    A.getMonth() === b.getMonth() &&
    A.getDate() === b.getDate()
  );
}

export default function StreakWidget({ backTo = "/dashboard", isDark = true }) {
  const navigate = useNavigate();
  const { current = 0, best = 0, lastCheckIn = null, checkIn, reset, recordActivity } = useStreaks();
  const { mode } = useTheme();

  const primaryClass =
    "bg-[var(--mudo-theme-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] hover:brightness-110";
  const softPrimaryClass =
    mode === "dark"
      ? "bg-[var(--mudo-primary-soft,#2c1f19)] text-[var(--mudo-onPrimary,#fdf7f0)]"
      : "bg-[var(--mudo-primary-soft,#f2d6c4)] text-[var(--mudo-onPrimary,#0d1014)]";

  // Lightweight "heartbeat": when the tab becomes visible or user returns,
  // record activity (which will auto-check-in for today if needed).
  useEffect(() => {
    const ping = () => {
      if (typeof recordActivity === "function") recordActivity("heartbeat");
      else if (!sameLocalDay(lastCheckIn)) checkIn?.();
    };
    // initial
    ping();
    // on visibility change
    const onVis = () => document.visibilityState === "visible" && ping();
    document.addEventListener("visibilitychange", onVis);
    // periodic (very light)
    const id = setInterval(ping, 60 * 1000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, [recordActivity, checkIn, lastCheckIn]);

  const card = "bg-slate-800/60 rounded p-3";
  const kpi = "text-xl font-semibold tabular-nums";
  const fmt = useMemo(() => (ts) => (ts ? new Date(ts).toLocaleString() : "-"), []);

  return (
    <div className={`h-full w-full ${isDark ? "text-white" : "text-slate-900"}`}>
      <div className="bg-slate-900/60 rounded-lg p-4">
        <WidgetHeader
          title="Streak"
          to={backTo}
          right={
            <div className="flex gap-2">
              <button
                className={`px-2 py-1 rounded font-medium transition ${primaryClass}`}
                style={{ backgroundColor: "var(--mudo-theme-primary)", borderColor: "var(--mudo-theme-primary)" }}
                onClick={() => navigateTo(navigate, ROUTES.JOURNAL, { source: "StreakWidget:journalButton" })}
                title="Open Journal"
              >
                Journal
              </button>
              <button
                className={`px-2 py-1 rounded font-medium transition ${softPrimaryClass}`}
                onClick={() => navigateTo(navigate, ROUTES.INSPIRATION, { source: "StreakWidget:inspirationButton" })}
                title="Inspiration"
              >
                Inspiration
              </button>
            </div>
          }
        />

        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div className={`${card} col-span-2`}>
            <div className="text-slate-400">Current streak</div>
            <div className={`${kpi} text-3xl`}>
              {current} {current === 1 ? "day" : "days"}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {sameLocalDay(lastCheckIn)
                ? "Great - you've checked in today."
                : "No activity yet today (will auto-check when you add something)."}
            </div>
          </div>

          <div className={card}>
            <div className="text-slate-400">Best</div>
            <div className={kpi}>
              {best} {best === 1 ? "day" : "days"}
            </div>
          </div>

          <div className={card}>
            <div className="text-slate-400">Last check-in</div>
            <div className="text-sm">{fmt(lastCheckIn)}</div>
          </div>

          <div className="text-xs text-slate-400 col-span-2">
            Tip: adding a Journal entry (or other activity) will keep your streak alive automatically.
          </div>
        </div>
      </div>
    </div>
  );
}
